import React, { createContext, useState, useContext, useEffect } from 'react';
import { setAuthToken, getAuthToken, clearAuthToken } from '../utils/auth';
import { API_BASE_URL } from '../config/apiConfig';

interface User {
  id: number;
  username: string;
  is_staff: boolean;
  has_completed_intake?: boolean; // Now optional since we use the flow completion endpoint
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isAuthLoading: boolean; // Add loading state
  login: (token: string, userData: User) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Initialize loading state

  useEffect(() => {
    // Check for existing token and user data in localStorage
    const token = getAuthToken();
    const userDataString = localStorage.getItem('user');

    if (token && userDataString) {
      // If we already have a token in localStorage, trust it and load the user.
      try {
        const userData = JSON.parse(userDataString);
        console.log('AuthContext: Found existing token and user data');
        setUser(userData);
        setIsAuthLoading(false); // Stop loading if token is found
      } catch (error) {
        console.error('AuthContext: Error parsing auth data from localStorage:', error);
        clearAuthToken();
        localStorage.removeItem('user');
        setIsAuthLoading(false); // Stop loading on error
      }
    } else {
      // No existing authentication found
      console.log('AuthContext: No existing authentication found');
      setIsAuthLoading(false);
    }
  }, []); // The dependency array is empty, so this runs once on mount.

  const login = async (token: string, userData: User) => {
    return new Promise<void>((resolve) => {
      console.log('AuthContext: Storing token and user data...');
      setAuthToken(token); // This saves to localStorage
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsAuthLoading(false); // Ensure loading is set to false upon login
      console.log('AuthContext: Token and user data stored.');
      resolve();
    });
  };

  const logout = () => {
    clearAuthToken();
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!user,
      user,
      isAuthLoading, // Expose loading state
      login,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
